import { daysBetween, ordinal, todayStr } from "./format";

/**
 * Where a job lands right after an MC action, before the next refresh. These
 * mirror resolveStatus() in the backend McOpsDashboardService so a job does not
 * jump columns when the server state reloads.
 */

const READY_TO_CONFIRM_DAYS = 10;
const DAILY_ASSIGN_DAYS = 14;

/** A move that has started or finished keeps its status whatever the MC does. */
const underway = (status) =>
  status === "in-progress" ||
  status === "move-completed" ||
  String(status).startsWith("post-move");

/** The first service date booked (packing, loading, move...), else the move date. */
const earliestServiceDate = (job) => {
  const dates = Object.values(job.serviceDates || {}).filter(Boolean).sort();
  return dates[0] || job.packingDate || job.moveDate;
};

/** An introduced, unconfirmed local job: Ready to Confirm 10 days before its first service date. */
const afterIntroLocal = (job, today) =>
  daysBetween(today, earliestServiceDate(job)) <= READY_TO_CONFIRM_DAYS
    ? "ready-to-confirm"
    : "intro-done";

/** A local job nobody has called yet: a daily task within 2 weeks, a weekly one further out. */
const beforeAnyCall = (job, today) =>
  daysBetween(today, job.moveDate) <= DAILY_ASSIGN_DAYS ? "daily-assign" : "weekly-assign";

export const statusAfterIntro = (job, today = todayStr()) => {
  if (job.confirmCallDone) return job.status;
  if (job.moveType === "ld") return "ld-confirmed-weekly";
  // A client who asked to be called back waits in Ready to Confirm.
  if (job.callBackRequested) return "ready-to-confirm";
  return afterIntroLocal(job, today);
};

export const statusAfterConfirm = (job) =>
  job.moveType === "ld" ? "ld-confirmed-weekly" : "confirmed";

/**
 * After the Callback toggle on the Confirmation Call section: requested, the
 * job holds in Ready to Confirm until it is confirmed; withdrawn, it goes back
 * to wherever its calls had left it.
 */
export const statusAfterCallback = (job, today = todayStr()) => {
  if (job.moveType === "ld" || underway(job.status)) return job.status;
  // A callback holds the job in Ready to Confirm — and pulls an already
  // confirmed job back there until the callback is cleared.
  if (job.callBackRequested) return "ready-to-confirm";
  if (job.confirmCallDone) return "confirmed";
  if (job.introCallDone) return afterIntroLocal(job, today);
  return beforeAnyCall(job, today);
};

/** After the MC reviewed a change with the client: the job leaves Revised Confirmations. */
export const statusAfterReview = (job, today = todayStr()) => {
  if (job.moveType === "ld" || underway(job.status)) return job.status;
  if (job.confirmCallDone) return "confirmed";
  if (job.callBackRequested) return "ready-to-confirm";
  if (job.introCallDone) return afterIntroLocal(job, today);
  return beforeAnyCall(job, today);
};

/**
 * The section a card opens on. A New Daily Assignment opens on the Summary, so
 * the MC reads the job before calling.
 */
export const defaultTabFor = (job, columnTab) =>
  job.status === "daily-assign" ? "s1" : columnTab;

/** The highest numbered call attempt logged in `attempts` ({n}[]), 0 when none. */
export const highestAttempt = (attempts) =>
  (Array.isArray(attempts) ? attempts : []).reduce(
    (max, a) => (a && a.n > max ? a.n : max),
    0,
  );

/**
 * Card tag for the call attempts of the job's current stage: the post-move
 * call once the job is in Post Move, the introduction call before that.
 * "1st attempt called", or "" when none.
 */
export const attemptTag = (job) => {
  if (!job) return "";
  const inPostMove = String(job.status || "").startsWith("post-move");
  const n = highestAttempt(inPostMove ? job.postMoveAttempts : job.introAttempts);
  return n ? `${ordinal(n)} attempt called` : "";
};

const STATUS_LABELS = {
  "daily-assign": "New Daily Assignment",
  "confirmed-update": "Revised Confirmation",
  "ready-to-confirm": "Ready to Confirm",
  "weekly-assign": "Weekly Assignment",
  confirmed: "Confirmed",
  "intro-done": "Intro Call Completed",
  "in-progress": "Move in Progress",
  "move-completed": "Move Completed",
  "post-move-vip": "Post Move — Call Client",
  "post-move-auto": "Post Move — Automation",
  "ld-to-confirm": "Long Distance — To Be Confirmed",
  "ld-in-transit": "Long Distance — In Transit",
  "ld-window-ending": "End of Delivery Window",
  "ld-warehouse-received": "SIT Local",
  "ld-sit-confirmed": "SIT Local Confirmed",
  "ld-driver-delivery": "Driver Deliveries",
  "ld-confirmed-weekly": "Long Distance — Confirmed",
  "ld-delivery-local": "Delivery Day in Progress",
};

/** The queue a job sits in, in the words the boards use. */
export const statusLabel = (status) => STATUS_LABELS[status] || status;
